// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useBankUploadData } from '@client/banks/providers/bank-upload-provider';
import type { BankUploadStep } from '@client/banks/types/bank-upload';
import { hasBankUploadListData } from '@client/banks/utils/has-bank-upload-list-data';
import { useStepsData } from '@shared/ui-kit/hooks/use-steps';
import { useSnackbar } from '@shared/ui-kit/providers/snackbar-provider';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { buildReviewChecklist, hasPendingSections } from './build-review-checklist';

export const useReviewSummary = () => {
  const { t } = useTranslation();

  const {
    formData,
    onClose,
    resetFormData,
    submitBankUpload,
    isSubmittingBankUpload,
  } = useBankUploadData();

  const { navigate, reset } = useStepsData();

  const { enqueueSnackbar } = useSnackbar();

  const bankName = useMemo(
    () => formData.institution.name,
    [formData.institution.name],
  );

  const checklistItems = useMemo(
    () => buildReviewChecklist(formData, t),
    [formData, t],
  );

  const showPendingBanner = useMemo(
    () => hasPendingSections(checklistItems),
    [checklistItems],
  );

  const canSubmit = useMemo(() => hasBankUploadListData(formData), [formData]);

  const onReviewSection = useCallback(
    (listStep: BankUploadStep) => {
      navigate(listStep);
    },
    [navigate],
  );

  const onConfirm = useCallback(async () => {
    if (!hasBankUploadListData(formData)) {
      return false;
    }

    try {
      await submitBankUpload();

      enqueueSnackbar(
        t('aggregator.create-bank.submit.success', { ns: 'aggregator' }),
        { variant: 'success' },
      );

      resetFormData();

      reset();

      onClose();
    } catch {
      enqueueSnackbar(
        t('aggregator.create-bank.submit.error', { ns: 'aggregator' }),
        { variant: 'error' },
      );
    }

    return false;
  }, [
    enqueueSnackbar,
    formData,
    onClose,
    reset,
    resetFormData,
    submitBankUpload,
    t,
  ]);

  return {
    bankName,
    checklistItems,
    showPendingBanner,
    canSubmit,
    onReviewSection,
    onConfirm,
    isSaving: isSubmittingBankUpload,
  };
};
